/* eslint-disable @typescript-eslint/naming-convention,max-lines */
// internal-deps
import { PaymentMethod, NetBankingPaymentMethodMeta, CardPaymentMethodMeta } from "@swiggy-private/payment-ui";
import {
    CardDetails,
    JUSPAY_ENV,
    MARKETPLACE_BUSINESS_LINE,
    OnPayExtraParams,
    VALID_PAYMENT_GROUP_TYPES,
    VALID_PAYMENT_METHODS,
} from "@swiggy-private/payment-types";
import {
    getBridgeSdk,
    GlobalHeaderState,
    isNonEmptyString,
    NEWRELIC_TABLES,
    ProcessTransactionActionPayload,
    recordNREvent,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";
import { checkIfNumber, isEmpty } from "@swiggy-private/core-ui";

// helpers
import { getPaymentCardToken, getPaymentBankCode } from "./PaymentHelper";
import { getPartnerOrderIdFromPennyTxnResponse } from "./TransactionHelper";

// utils
import { serializeParams } from "@payment-client/utils/URI";
import { createAndSubmitForm } from "@payment-client/utils/Form";

// interfaces
import {
    JuspayNetBankingRedirectRequest,
    JuspaySavedCardRedirectRequest,
    JuspayRedirectResponse,
    JuspayNewCardRedirectRequest,
    JuspayDirectIntegrationToken,
} from "@payment-client/interfaces/JuspayRedirect";
import { ProcessOrderActionPayload } from "@payment-client/interfaces/Checkout";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { JuspayClientAuthTokenData, JuspaySdkFeatureAvailable } from "@payment-client/interfaces/Juspay";

// apis
import { juspayTokenize, juspayRedirect } from "@payment-client/apis/JuspayRedirect";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";

// constants
import {
    DEFAULT_JUSPAY_MERCHANTID,
    DEFAULT_JUSPAY_V3_TENANT,
    INIT_JUSPAY_TIMEOUT,
    JUSPAY_SDK_REQUEST_FAIL_INSTANCE,
} from "@payment-client/constants/Juspay";
import {
    DEFAULT_MAX_IOS_VERSION_FOR_JUSPAY_REDIRECT_FLOW,
    DEFAULT_MAX_IOS_VERSION_FOR_JUSPAY_REDIRECT_FLOW_DASH,
    DEFAULT_MAX_IOS_VERSION_FOR_JUSPAY_REDIRECT_FLOW_OTHER_BLS,
} from "@payment-client/constants/Card";
import { getPartnerOrderIdFromOrderData } from "./OrderHelper";
import { isBusinessLineSwiggyMoney, isIos, isOriginalBusinessLineSwiggyMoney } from "./Misc";

export function sanitizeDirectIntegrationToken(
    token: Partial<JuspayDirectIntegrationToken> | undefined,
): JuspayDirectIntegrationToken | undefined {
    if (!token) return undefined;
    const sanitized: Partial<JuspayDirectIntegrationToken> = {};

    const addIfString = (key: keyof JuspayDirectIntegrationToken): void => {
        const value = token[key];
        if (typeof value === "string" && value.trim()) sanitized[key] = value.trim();
    };

    addIfString("type");
    addIfString("requestor_id");
    addIfString("provider_reference_id");
    addIfString("provider_category");
    addIfString("provider");
    addIfString("par");

    return Object.keys(sanitized).length > 0 ? (sanitized as JuspayDirectIntegrationToken) : undefined;
}

/**
 * Creates a handler which can be used to redirect to juspay using
 * given data.
 *
 * @param data - form data
 * @returns the handler
 */
const createRedirectToJusPayHandler = (data: {
    method: string;
    url: string;
    params: { [key in string]: string };
}): VoidFunction => {
    const { method, url, params = null } = data || {};
    if (!method || !url || (method == "POST" && !params)) {
        throw new Error("Invalid arguments passed to redirect to Juspay: " + data);
    }

    return (): void => {
        try {
            createAndSubmitForm(data);
        } catch (err) {
            throw new Error("Error in form submitting to juspay" + err);
        }
    };
};

export const getCardTokenizeValue = (
    details: CardDetails | CardPaymentMethodMeta,
    extraParams?: OnPayExtraParams & { saveCard?: boolean },
    isNonSecureCard?: boolean,
): boolean | null => {
    // tokenize support = false --> don't send juspay tokenize param or send null
    // tokenize support = true --> send juspay tokenize = true when save card is true
    // tokenize support = true --> send juspay tokenize = false when save card is false

    if (isNonSecureCard || details.tokenizeSupport !== true) {
        return null;
    }

    return extraParams != null && !!extraParams.saveCard;
};

const handleNewCardRedirect = async (
    payMethod: PaymentMethod<CardPaymentMethodMeta>,
    orderId: string,
    extraParams: Record<string, unknown>,
    juspayMeta?: GetPaymentMethodsResponse["juspayMeta"],
): Promise<JuspayNewCardRedirectRequest> => {
    const merchantId = juspayMeta?.merchantId || DEFAULT_JUSPAY_MERCHANTID;

    const data = {
        card_number: String(extraParams.cardNumber).replace(/\s+/g, ""),
        card_exp_year: extraParams.year,
        card_exp_month: extraParams.month,
        card_security_code: extraParams.cvv,
        merchant_id: merchantId,
        name_on_card: extraParams.name,
        card_nickname: String(extraParams.nickName || ""),
    };

    const serializedPaymentData = serializeParams(data);

    try {
        const response = await juspayTokenize(serializedPaymentData, merchantId);
        return {
            order_id: orderId,
            merchant_id: merchantId,
            payment_method_type: "CARD",
            card_token: response.token,
            redirect_after_payment: true,
            format: "json",
            card_security_code: String(data.card_security_code),
            save_to_locker: !!extraParams.saveCard,
            tokenize: getCardTokenizeValue(payMethod.meta, extraParams),
            card_nickname: String(extraParams.nickName || ""),
            ...(extraParams.isEmi === true && {
                is_emi: extraParams.isEmi,
                emi_bank: String(extraParams.emiBank || ""),
                emi_type: String(extraParams.emiType || ""),
                emi_tenure: Number(extraParams.emiTenure || 0),
            }),
        };
    } catch (err) {
        throw new Error("Error in juspay(redirect) tokenization of new card flow:" + err);
    }
};

const handleNetBankingRedirect = (
    method: PaymentMethod<NetBankingPaymentMethodMeta>,
    orderId: string,
    _extraParams: Record<string, unknown>,
    juspayMeta?: GetPaymentMethodsResponse["juspayMeta"],
): Promise<JuspayNetBankingRedirectRequest> => {
    const merchantId = juspayMeta?.merchantId || DEFAULT_JUSPAY_MERCHANTID;
    return Promise.resolve({
        order_id: orderId,
        merchant_id: merchantId,
        payment_method_type: "NB",
        payment_method: getPaymentBankCode(method),
        redirect_after_payment: true,
        format: "json",
    });
};

export const handleSavedCardRedirect = (
    payMethod: PaymentMethod<CardPaymentMethodMeta>,
    orderId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extraParams: Record<string, unknown>,
    juspayMeta?: GetPaymentMethodsResponse["juspayMeta"],
): Promise<JuspaySavedCardRedirectRequest> => {
    const merchantId = juspayMeta?.merchantId || DEFAULT_JUSPAY_MERCHANTID;
    const cardParams: JuspaySavedCardRedirectRequest = {
        order_id: orderId,
        merchant_id: merchantId,
        payment_method_type: "CARD",
        card_token: getPaymentCardToken(payMethod),
        redirect_after_payment: true,
        format: "json",
        card_security_code: String(extraParams.cvv),
        save_to_locker: false,
        tokenize: getCardTokenizeValue(payMethod.meta, extraParams),
        card_nickname: String(extraParams.nickName || ""),
        ...(extraParams.isEmi === true && {
            is_emi: extraParams.isEmi,
            emi_bank: String(extraParams.emiBank || ""),
            emi_type: String(extraParams.emiType || ""),
            emi_tenure: Number(extraParams.emiTenure || 0),
        }),
    };

    if (!isEmpty(extraParams?.token)) {
        const sanitizedToken = sanitizeDirectIntegrationToken(
            (extraParams?.token as Partial<JuspayDirectIntegrationToken>) || undefined,
        );
        if (sanitizedToken) {
            cardParams["token"] = sanitizedToken;
        }
    }

    if (!payMethod?.meta?.cvvMandatory) {
        delete cardParams["card_security_code"];
    }
    return Promise.resolve(cardParams);
};

const handleSavedCardRedirectTxnFlow = (
    cardDetails: CardDetails,
    orderId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extraParams: Record<string, unknown>,
    juspayMeta?: ProcessTransactionActionPayload["merchantInfo"],
    _isNotSecureCardFlow?: boolean,
    // eslint-disable-next-line max-params
): Promise<JuspaySavedCardRedirectRequest> => {
    const merchantId = juspayMeta?.merchantId || DEFAULT_JUSPAY_MERCHANTID;
    return Promise.resolve({
        order_id: orderId,
        merchant_id: merchantId,
        payment_method_type: "CARD",
        card_token: cardDetails.cardToken,
        redirect_after_payment: true,
        format: "json",
        card_security_code: String(extraParams.cvv),
        save_to_locker: false,
        tokenize: true,
        card_nickname: String(extraParams.nickName || ""),
        // for direct integration changes
        name_on_card: cardDetails.nameOnCard ? String(cardDetails.nameOnCard) : "",
        token: (extraParams.token || {}) as JuspayDirectIntegrationToken,
    });
};

const handleNewCardRedirectTxnFlow = async (
    cardDetails: CardDetails,
    orderId: string,
    extraParams: Record<string, unknown>,
    juspayMeta?: ProcessTransactionActionPayload["merchantInfo"],
    isNonSecureCard?: boolean,
    // eslint-disable-next-line max-params
): Promise<JuspayNewCardRedirectRequest> => {
    const merchantId = juspayMeta?.merchantId || DEFAULT_JUSPAY_MERCHANTID;

    const data = {
        card_number: String(extraParams.cardNumber).replace(/\s+/g, ""),
        card_exp_year: extraParams.year,
        card_exp_month: extraParams.month,
        card_security_code: extraParams.cvv,
        merchant_id: merchantId,
        name_on_card: extraParams.name,
        card_nickname: String(extraParams.nickName || ""),
    };

    const serializedPaymentData = serializeParams(data);

    try {
        const response = await juspayTokenize(serializedPaymentData, merchantId);
        return {
            order_id: orderId,
            merchant_id: merchantId,
            payment_method_type: "CARD",
            card_token: response.token,
            redirect_after_payment: true,
            format: "json",
            card_security_code: String(data.card_security_code),
            save_to_locker: true,
            tokenize: getCardTokenizeValue(cardDetails, extraParams, isNonSecureCard),
            card_nickname: String(extraParams.nickName || ""),
        };
    } catch (err) {
        throw new Error("Error in juspay(redirect) tokenization of new card flow:" + err);
    }
};

export const payUsingJusPayRedirectFlow = async (
    processOrderData: ProcessOrderActionPayload,
    methodType: string,
): Promise<{ internalRedirect: boolean; orderId: string; redirectFn: VoidFunction }> => {
    const {
        order,
        placeOrderData: { paymentMethodData, extraParams },
        juspayMeta,
    } = processOrderData;

    /** OrderId and PaymentPartnerOrderId are same for normal orders.
     * But we they are different in edit order (Eg: OrderId: 79879281967, paymentTxId: 79879281967E1)
     * We need to consider payment txn id here to make jus pay work as
     * juspay uses txn id in '/txns' api call to validate payments
     */
    const orderId = getPartnerOrderIdFromOrderData(order);
    const juspayMetaData = order?.juspayMeta?.juspayMetaData;
    const directIntegrationParams = {
        token: {
            type: juspayMetaData?.tokenType,
            requestor_id: juspayMetaData?.requesterId,
            provider_reference_id: juspayMetaData?.providerReferenceId,
            provider_category: juspayMetaData?.providerCategory,
            provider: juspayMetaData?.provider,
            par: (paymentMethodData?.meta?.token as unknown as { par?: string })?.par || "",
        },
    };

    const JUSPAY_PAYMENT_METHOD_HANDLERS = {
        [VALID_PAYMENT_METHODS.NETBANKING]: handleNetBankingRedirect,
        [VALID_PAYMENT_METHODS.CARD]: handleSavedCardRedirect,
        [VALID_PAYMENT_METHODS.NEW_CARD]: handleNewCardRedirect,
    };

    const paymentMethodHandler = JUSPAY_PAYMENT_METHOD_HANDLERS[methodType];
    if (typeof paymentMethodHandler != "function") {
        throw new Error(
            "Error in juspay redirect flow, paymentMethodHandler is not a function: " + paymentMethodHandler,
        );
    }

    try {
        const paymentData = await paymentMethodHandler(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            paymentMethodData as any,
            orderId,
            { ...(extraParams || {}), ...directIntegrationParams },
            juspayMeta,
        );

        const serializedPaymentData = serializeParams(paymentData);

        const merchantId = juspayMeta?.merchantId || DEFAULT_JUSPAY_MERCHANTID;

        const redirectFn = await juspayRedirect(serializedPaymentData, merchantId).then(
            (juspayRedirectResponse: JuspayRedirectResponse) => {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                    eventSubType: "juspay_redirect_flow",
                    type: "JUSPAY_REDIRECT_FLOW",
                });
                const formData = juspayRedirectResponse?.payment?.authentication;
                return createRedirectToJusPayHandler(formData);
            },
        );

        return { internalRedirect: true, orderId, redirectFn };
    } catch (err) {
        throw new Error("Error while making payment using juspay redirect: " + err);
    }
};

export const processTxnUsingJusPayRedirectFlow = async (
    processTransactionData: ProcessTransactionActionPayload,
    methodType: string,
    isNonSecureCard?: boolean,
): Promise<{ internalRedirect: boolean; orderId: string; redirectFn: VoidFunction }> => {
    const {
        transaction,
        merchantInfo,
        transactionReqData: { cardDetails, extraParams },
    } = processTransactionData;

    const orderId = getPartnerOrderIdFromPennyTxnResponse(transaction);
    const { tokenInfo } = transaction;
    const directIntegrationParams = {
        token: {
            type: tokenInfo?.juspayInfo?.tokenType,
            requestor_id: tokenInfo?.juspayInfo?.requesterId,
            provider_reference_id: tokenInfo?.juspayInfo?.providerReferenceId,
            provider_category: tokenInfo?.juspayInfo?.providerCategory,
            provider: tokenInfo?.juspayInfo?.provider,
            par: extraParams.par || "",
        },
    };

    const JUSPAY_PAYMENT_METHOD_HANDLERS = {
        [VALID_PAYMENT_METHODS.CARD]: handleSavedCardRedirectTxnFlow,
        [VALID_PAYMENT_METHODS.NEW_CARD]: handleNewCardRedirectTxnFlow,
    };

    const paymentMethodHandler = JUSPAY_PAYMENT_METHOD_HANDLERS[methodType];
    if (typeof paymentMethodHandler != "function") {
        throw new Error(
            "Error in juspay redirect flow, paymentMethodHandler is not a function: " + paymentMethodHandler,
        );
    }

    try {
        const paymentData = await paymentMethodHandler(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            cardDetails as any,
            orderId,
            { ...(extraParams || {}), ...directIntegrationParams },
            merchantInfo,
            isNonSecureCard ?? false,
        );

        const serializedPaymentData = serializeParams(paymentData);
        const merchantId = merchantInfo?.merchantId || DEFAULT_JUSPAY_MERCHANTID;
        const redirectFn = await juspayRedirect(serializedPaymentData, merchantId).then(
            (juspayRedirectResponse: JuspayRedirectResponse) => {
                const formData = juspayRedirectResponse?.payment?.authentication;
                return createRedirectToJusPayHandler(formData);
            },
        );

        return { internalRedirect: true, orderId, redirectFn };
    } catch (err) {
        throw new Error("Error while making payment using juspay redirect: " + err);
    }
};

export const getMerchantId = (paymentResponse?: GetPaymentMethodsResponse | null): string =>
    paymentResponse?.juspayMeta?.merchantId ?? DEFAULT_JUSPAY_MERCHANTID;

export const camelizeCase = (str: string) => {
    return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
};

/**
 * Resolves `clientAuthTokenData` to a single `JuspayClientAuthTokenData`.
 * When V3 multi-tenant map is stored in state, picks the default tenant token
 * (DEFAULT_JUSPAY_V3_TENANT → "SW-FOOD"), falling back to the first available entry.
 */
export function resolveClientAuthTokenData(
    data: JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData> | undefined,
): JuspayClientAuthTokenData | undefined {
    if (!data) return undefined;
    if ("clientAuthToken" in data) return data as JuspayClientAuthTokenData;
    const map = data as Record<string, JuspayClientAuthTokenData>;
    return map[DEFAULT_JUSPAY_V3_TENANT] ?? map[camelizeCase(DEFAULT_JUSPAY_V3_TENANT)] ?? Object.values(map)[0];
}

/**
 * Resolves the Juspay client auth token for a specific tenant from the V3 token map.
 * Falls back to DEFAULT_JUSPAY_V3_TENANT and then the first available entry.
 */
export function resolveClientAuthTokenDataForMerchantId(
    data: JuspayClientAuthTokenData | Record<string, JuspayClientAuthTokenData> | undefined,
    merchantId: string,
    defaultMerchantId: string,
): JuspayClientAuthTokenData | undefined {
    if (!data) return undefined;
    if ("clientAuthToken" in data) return data as JuspayClientAuthTokenData;
    const map = Object.values(data) as JuspayClientAuthTokenData[];
    let authToken = map.find((dat) => dat.mid === merchantId);

    if (isEmpty(authToken)) {
        authToken = map.find((dat) => dat.mid === defaultMerchantId);
    }

    if (isEmpty(authToken)) {
        authToken = map.find((dat) => dat.mid === DEFAULT_JUSPAY_MERCHANTID);
    }
    return authToken;
}

/**
 * Resolves the Juspay V3 tenant key for a given GPO response.
 *
 * Priority:
 *  1. `tenant` on the matching payment group (groupName match or first group with a tenant)
 *  2. Root-level `tenant` in the GPO response
 *  3. DEFAULT_JUSPAY_V3_TENANT ("SW-FOOD")
 */
export function resolveMerchantId(paymentResponse: GetPaymentMethodsResponse, groupName: string): string {
    const isWalletGroup =
        groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.WALLET ||
        groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.WALLET_WEB;

    const groups = paymentResponse.paymentGroup;
    let merchantId = DEFAULT_JUSPAY_MERCHANTID;

    if (groups?.length) {
        const group = isWalletGroup
            ? groups.find(
                  (g) =>
                      g.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.WALLET ||
                      g.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.WALLET_WEB,
              )
            : groups.find((g) => g.groupName.toUpperCase() === groupName.toUpperCase());
        merchantId = group?.juspayMeta?.merchantId || merchantId;
    }

    merchantId = paymentResponse.juspayMeta.merchantId || merchantId;
    return merchantId;
}

export const isJuspaySdkAvailable = async (
    clientAuthTokenData: JuspayClientAuthTokenData,
    merchantId?: string,
    forceEnableSDK?: boolean,
    tenant?: string,
): Promise<{
    isSdkAvailable: boolean;
    featuresAvailability?: JuspaySdkFeatureAvailable;
}> => {
    // We only want SDK flow in SwiggyMoney for independent recharge/gifting flow and
    // not for add-money through payments page since multiple tenants opening multiple
    // juspay instances in a single session is not currently supported
    const isCheckoutPageflowForSM = isBusinessLineSwiggyMoney() && !isOriginalBusinessLineSwiggyMoney();
    if (!isNonEmptyString(merchantId) || isCheckoutPageflowForSM) {
        return { isSdkAvailable: false };
    }

    const data = await getBridgeSdk().isJuspaySdkAvailable({
        ...clientAuthTokenData,
        forceEnableSDK,
        merchantId,
        ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
    });
    if (data && data.isAvailable) {
        return getBridgeSdk()
            .initJuspaySdk(
                {
                    ...clientAuthTokenData,
                    forceEnableSDK,
                    merchantId,
                    environment: JUSPAY_ENV.PROD,
                    ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
                },
                INIT_JUSPAY_TIMEOUT,
            )
            .then((_) => {
                return {
                    isSdkAvailable: true,
                    featuresAvailability: data.featuresAvailability || undefined,
                };
            })
            .catch((_) => {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.JUSPAY_SDK_NOT_AVAILABLE,
                    failInstance: JUSPAY_SDK_REQUEST_FAIL_INSTANCE.INIT,
                });
                return { isSdkAvailable: false };
            });
    }
    recordNREvent({
        eventType: NEWRELIC_TABLES.JUSPAY_SDK_NOT_AVAILABLE,
        failInstance: JUSPAY_SDK_REQUEST_FAIL_INSTANCE.AVAILABLE,
    });
    return { isSdkAvailable: false };
};

export function getJuspaySdkFeatureAvailability(
    groupName: string,
    isJuspaySdkAvailable: boolean,
    featuresAvailability?: JuspaySdkFeatureAvailable,
): boolean {
    //Backward compatiblity if we dont get featuresAvailability in older version of APPs
    if (!isJuspaySdkAvailable || isEmpty(featuresAvailability)) {
        return isJuspaySdkAvailable;
    }

    switch (groupName.toUpperCase()) {
        case VALID_PAYMENT_GROUP_TYPES.CARD:
        case VALID_PAYMENT_GROUP_TYPES.FOOD_CARD:
            return isJuspaySdkAvailable && featuresAvailability.cardEnabled;
        case VALID_PAYMENT_GROUP_TYPES.CRED:
            return isJuspaySdkAvailable && featuresAvailability.credEnabled;
        case VALID_PAYMENT_GROUP_TYPES.INAPP_UPI:
            return isJuspaySdkAvailable && featuresAvailability.inAppUpiEnabled;
        case VALID_PAYMENT_GROUP_TYPES.NETBANKING:
            return isJuspaySdkAvailable && featuresAvailability.netBankingEnabled;
        default: {
            return isJuspaySdkAvailable;
        }
    }
}

// version check for juspay sdk flow in IOS
export function shouldUseJuspayRedirectFlow(): boolean {
    // food Version Check
    const defaultMaxVersionForRedirectFlow =
        process.env.DEFAULT_MAX_IOS_VERSION_FOR_JUSPAY_REDIRECT_FLOW ||
        DEFAULT_MAX_IOS_VERSION_FOR_JUSPAY_REDIRECT_FLOW;
    // dash Version Check
    const defaultMaxVersionForRedirectFlowDash =
        process.env.DEFAULT_MAX_IOS_VERSION_FOR_JUSPAY_REDIRECT_FLOW_DASH ||
        DEFAULT_MAX_IOS_VERSION_FOR_JUSPAY_REDIRECT_FLOW_DASH;
    // Other BLs Version Check
    const defaultMaxVersionForRedirectFlowAllBls =
        process.env.DEFAULT_MAX_IOS_VERSION_FOR_JUSPAY_REDIRECT_FLOW_OTHER_BLS ||
        DEFAULT_MAX_IOS_VERSION_FOR_JUSPAY_REDIRECT_FLOW_OTHER_BLS;

    const versionCode = GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode);
    const businessLine = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine);

    if (!checkIfNumber(versionCode) || isEmpty(versionCode) || isEmpty(businessLine)) {
        return isIos();
    } else if (businessLine.toLowerCase() === MARKETPLACE_BUSINESS_LINE.REGULAR) {
        return isIos() && Number(versionCode) <= Number(defaultMaxVersionForRedirectFlow);
    } else if (businessLine.toLowerCase() === MARKETPLACE_BUSINESS_LINE.DASH) {
        return isIos() && Number(versionCode) <= Number(defaultMaxVersionForRedirectFlowDash);
    }
    return isIos() && Number(versionCode) < Number(defaultMaxVersionForRedirectFlowAllBls);
}
